import { type FormEvent } from "react"
import { Link, useForm } from "@inertiajs/react"
import { Box, Button, Card, Flex, Heading, Text, TextArea } from "@radix-ui/themes"
import { z } from "zod"
import { PromptFormSchema } from "@/schemas"

const PropsSchema = z.object({
  project: z.object({
    id: z.number(),
    name: z.string(),
  }),
  form: PromptFormSchema.nullish(),
})

type Props = z.infer<typeof PropsSchema>

function formatError(error: string | undefined) {
  return error ?? null
}

export default function NewPromptPage(rawProps: Props) {
  const props = PropsSchema.parse(rawProps)

  const { data, setData, post, processing, errors } = useForm({
    text: props.form?.text ?? "",
  })

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    post(`/projects/${props.project.id}/prompts`)
  }

  return (
    <Flex justify="center">
      <Box width="100%" style={{ maxWidth: "640px" }}>
        <Flex justify="between" align="center" mb="4">
          <Heading size="7">Create prompt</Heading>
          <Button variant="soft" color="gray" asChild>
            <Link href={`/projects/${props.project.id}`}>Back to project</Link>
          </Button>
        </Flex>

        <Card size="4">
          <Box asChild>
            <form onSubmit={submit}>
              <Flex direction="column" gap="4">
                <label>
                  <Text as="div" size="2" weight="medium" mb="1">
                    Content
                  </Text>
                  <TextArea
                    value={data.text}
                    onChange={(event) => setData("text", event.currentTarget.value)}
                    placeholder="Write the prompt content"
                    rows={6}
                    required
                  />
                  {formatError(errors.text) && (
                    <Text as="div" size="1" color="red" mt="1">
                      {formatError(errors.text)}
                    </Text>
                  )}
                </label>

                <Flex gap="3" justify="end">
                  <Button type="submit" variant="classic" disabled={processing}>
                    {processing ? "Creating prompt..." : "Create prompt"}
                  </Button>
                </Flex>
              </Flex>
            </form>
          </Box>
        </Card>
      </Box>
    </Flex>
  )
}
